import { supabase } from '../supabaseClient';
import { filterVisibleStatusOptions } from '../middleware/permissions';
import { getCachedUserAndProfile } from '../utils/userCache';

// 系統保護狀態 - 不可刪除
const SYSTEM_PROTECTED_STATUSES = [
  'open',
  'in_progress',
  'rd_fixed',
  'rd_verify',
  'verify',
  'completed',
  'closed',
  'blocked',
  'tbd',
];

/**
 * 檢查是否為系統保護狀態
 */
export const isSystemProtectedStatus = (value) => {
  return SYSTEM_PROTECTED_STATUSES.includes(value);
};

/**
 * 取得所有狀態選項
 * @param {number|null} departmentId - 部門 ID（用於過濾）
 */
export const getStatusOptions = async (departmentId = null) => {
  try {
    // 使用快取避免重複查詢
    const { profile: currentProfile } = await getCachedUserAndProfile(supabase);
    
    if (!currentProfile) return { data: [], error: new Error('找不到使用者資料') };
    
    const { data: allStatuses, error } = await supabase
      .from('status_options')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('❌ statusOptions 查詢錯誤:', error);
      throw error;
    }
    
    // 使用 middleware 過濾
    const visibleStatuses = filterVisibleStatusOptions(allStatuses || [], currentProfile, departmentId);
    
    return { data: visibleStatuses, error: null };
  } catch (error) {
    console.error('Error fetching status options:', error);
    return { data: null, error };
  }
};

/**
 * Beta 模式專用：取得狀態選項
 * 優先順序：系統預設 (company_id IS NULL) + 公司專屬覆蓋 + 部門專屬
 * 若公司有同 value 的覆蓋，以公司版為準
 */
export const getBetaStatusOptions = async (companyId, userDepartmentId) => {
  try {
    // 撈系統預設 (company_id IS NULL) + 該公司專屬 (company_id = X)
    // department 篩選：共用 (NULL) + 使用者部門
    const deptFilter = userDepartmentId
      ? `department_id.is.null,department_id.eq.${userDepartmentId}`
      : 'department_id.is.null';

    const { data, error } = await supabase
      .from('status_options')
      .select('*')
      .or(`company_id.is.null,company_id.eq.${companyId}`)
      .or(deptFilter)
      .order('display_order', { ascending: true });
    if (error) throw error;

    // 若公司有同 value 的覆蓋，以公司版為準（company_id 非 null 優先）
    const merged = [];
    const seen = new Set();
    // 先放公司專屬（優先）
    (data || []).filter((s) => s.company_id).forEach((s) => {
      seen.add(s.value);
      merged.push(s);
    });
    // 再放系統預設（未被覆蓋的）
    (data || []).filter((s) => !s.company_id).forEach((s) => {
      if (!seen.has(s.value)) merged.push(s);
    });

    merged.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    return { data: merged, error: null };
  } catch (error) {
    console.error('Error fetching beta status options:', error);
    return { data: [], error };
  }
};

/**
 * 建立新狀態選項
 */
export const createStatusOption = async (statusData) => {
  try {
    // 檢查是否已存在相同的 value
    const { data: existingStatuses, error: checkError } = await supabase
      .from('status_options')
      .select('value')
      .eq('value', statusData.value);

    if (checkError) throw checkError;

    if (existingStatuses && existingStatuses.length > 0) {
      return { 
        data: null, 
        error: { message: `狀態值「${statusData.value}」已存在，不可重複建立` }
      };
    }

    const { data, error } = await supabase
      .from('status_options')
      .insert({
        value: statusData.value,
        label: statusData.label,
        color: statusData.color,
        display_order: statusData.display_order ?? 99,  // 使用 ?? 而不是 ||
        department_id: statusData.department_id,  // 必須傳遞 department_id！
        company_id: statusData.company_id  // 必須傳遞 company_id！
      })
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creating status option:', error);
    return { data: null, error };
  }
};

/**
 * 更新狀態選項
 */
export const updateStatusOption = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('status_options')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating status option:', error);
    return { data: null, error };
  }
};

/**
 * 刪除狀態選項
 */
export const deleteStatusOption = async (id) => {
  try {
    // 先查詢該狀態的 value，檢查是否為系統保護狀態
    const { data: status, error: fetchError } = await supabase
      .from('status_options')
      .select('value, label')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    // 檢查是否為系統保護狀態
    if (status && isSystemProtectedStatus(status.value)) {
      return { 
        error: { 
          message: `「${status.label} (${status.value})」是系統保護狀態，不可刪除` 
        }
      };
    }

    const { error } = await supabase
      .from('status_options')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting status option:', error);
    return { error };
  }
};

/**
 * 批量更新多個狀態選項的 display_order
 * @param {Array} updates - 格式: [{ id, value, label, color, display_order, ... }, ...]
 */
export const batchUpdateDisplayOrder = async (updates) => {
  try {
    // 使用 upsert 批量更新（1 次 API 呼叫）
    // 傳入完整物件，這樣不會覆蓋其他欄位
    const { data, error } = await supabase
      .from('status_options')
      .upsert(updates, { onConflict: 'id', ignoreDuplicates: false })
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error batch updating display order:', error);
    return { data: null, error };
  }
};

